/**
 * Pre-launch gate. When on, random web visitors see the reading homepage and the
 * waitlist page; the real app (/read, /home, …) stays hidden. Flip WAITLIST_GATE
 * to false (or build with VITE_WAITLIST_GATE=off) to reopen.
 *
 * Let through without a waitlist:
 *  - the Wall OS Android / iOS apps (their WebView appends "WallOSApp/<os>" to
 *    the user agent; Play testers and Google's reviewers use the app),
 *  - testers who opened an invite link (?unlock=<code>) once — remembered in a
 *    cookie (shared across wallos.io subdomains) and in localStorage,
 *  - localhost.
 */

export const WAITLIST_GATE = true;

// Not secrets in the cryptographic sense (they ship in the bundle) — they only
// keep a curious crowd out. Rotate freely; rotating logs testers out.
export const UNLOCK_CODE = '0wfw47ruyr8ndt82q2ta5adc';
export const TESTER_CODE = 'moonlit-reader-7k3q';
const VALID_CODES = new Set([UNLOCK_CODE, TESTER_CODE]);

export const TESTER_INVITE_URL = `https://www.wallos.io/read?unlock=${TESTER_CODE}`;

const STORAGE_KEY = 'wallos_unlock';
const COOKIE_NAME = 'wallos_unlock';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function isValidUnlockCode(code: string | null | undefined): boolean {
  return !!code && VALID_CODES.has(code);
}

/** The Wall OS phone / TV app: a react-native-webview pointed at the site. */
export function isWallOsAppUserAgent(ua: string): boolean {
  if (/WallOSApp\//.test(ua)) return true;
  // Social in-app browsers (TikTok, Instagram, Facebook, X…) are NOT the app.
  if (/musical_ly|Bytedance|TikTok|Instagram|FBAN|FBAV|Twitter|Snapchat|Pinterest|Line\//i.test(ua)) return false;
  // Builds before the WallOSApp token: Android WebView ("; wv)") and iOS
  // WKWebView (WebKit + iPhone, no "Safari/" token, not Chrome/Firefox/Edge iOS).
  if (/Android/.test(ua) && /\bwv\b/.test(ua)) return true;
  if (/(iPhone|iPad|iPod)/.test(ua) && /AppleWebKit/.test(ua) && !/Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) return true;
  return false;
}

export function readCookie(cookieHeader: string, name: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const [k, ...v] = part.trim().split('=');
    if (k === name) {
      try { return decodeURIComponent(v.join('=')); } catch { return null; }
    }
  }
  return null;
}

export function unlockCookie(code: string, host: string, secure: boolean): string {
  const domain = /(^|\.)wallos\.io$/.test(host) ? '; Domain=.wallos.io' : '';
  return `${COOKIE_NAME}=${encodeURIComponent(code)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${domain}${secure ? '; Secure' : ''}`;
}

export function clearUnlockCookie(host: string): string {
  const domain = /(^|\.)wallos\.io$/.test(host) ? '; Domain=.wallos.io' : '';
  return `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${domain}`;
}

export interface GateInput {
  gateOn: boolean;
  host: string;
  userAgent: string;
  unlockParam: string | null;
  cookie: string;
  stored: string | null;
}

export type GateDecision =
  | { gated: false; reason: 'gate-off' | 'localhost' | 'app' | 'invite' | 'remembered'; remember?: string }
  | { gated: true; forget?: boolean };

/** Pure decision so it can be unit tested; `isGated()` applies the side effects. */
export function decideGate(i: GateInput): GateDecision {
  if (!i.gateOn) return { gated: false, reason: 'gate-off' };
  if (i.host === 'localhost' || i.host === '127.0.0.1' || i.host.endsWith('.local')) return { gated: false, reason: 'localhost' };
  if (isWallOsAppUserAgent(i.userAgent)) return { gated: false, reason: 'app' };
  if (i.unlockParam === 'off') return { gated: true, forget: true };
  if (isValidUnlockCode(i.unlockParam)) return { gated: false, reason: 'invite', remember: i.unlockParam! };
  if (isValidUnlockCode(readCookie(i.cookie, COOKIE_NAME)) || isValidUnlockCode(i.stored)) {
    return { gated: false, reason: 'remembered' };
  }
  return { gated: true };
}

function gateOn(): boolean {
  const flag = String((import.meta as any).env?.VITE_WAITLIST_GATE ?? '').toLowerCase();
  if (flag === 'off' || flag === 'false' || flag === '0') return false;
  return WAITLIST_GATE;
}

let cached: boolean | null = null;

export function isGated(): boolean {
  if (cached !== null) return cached;
  if (typeof window === 'undefined') return false;

  const host = window.location.hostname;
  const params = new URLSearchParams(window.location.search);
  let stored: string | null = null;
  try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* private mode */ }

  const d = decideGate({
    gateOn: gateOn(),
    host,
    userAgent: navigator.userAgent || '',
    unlockParam: params.get('unlock'),
    cookie: document.cookie || '',
    stored,
  });

  if (d.gated && d.forget) {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    document.cookie = clearUnlockCookie(host);
  }
  if (!d.gated && d.remember) {
    try { localStorage.setItem(STORAGE_KEY, d.remember); } catch { /* ignore */ }
    document.cookie = unlockCookie(d.remember, host, window.location.protocol === 'https:');
  }
  if (!d.gated && d.reason === 'remembered' && !readCookie(document.cookie || '', COOKIE_NAME) && stored) {
    document.cookie = unlockCookie(stored, host, window.location.protocol === 'https:');
  }
  if (params.has('unlock')) {
    // Drop the code from the address bar so it isn't screenshotted.
    params.delete('unlock');
    const qs = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }

  cached = d.gated;
  return cached;
}
